import type { JSX } from 'react';
import { Alert, Anchor, Badge, Center, Group, Loader, Stack, Table, Text, Tooltip } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ApiError, api, overviewApi, scheduleApi, type ScheduleOverviewRow, undoPaths } from '../../api';
import { formatDate, formatHours, formatPercent } from '../../format';
import { PageHeader, Section } from '../../ui/Section';
import { guardedStep, useUndo } from '../../ui/undo';
import { DateCell } from './DateCell';
import { linkTo } from '../../ui/links';

const DAY = 86_400_000;
const time = (iso: string): number => Date.parse(`${iso}T00:00:00Z`);
const signed = (n: number): string => (n > 0 ? `+${n}` : String(n));

/**
 * Dates and durations of all projects side by side: planned start and finish
 * against the target and the active baseline, on one shared time axis, with
 * progress and - where time is recorded - hours against budget. The target
 * finish can be set here; it is the same field as in the project's plan.
 */
export function ScheduleOverviewPage(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const undo = useUndo();
  const me = queryClient.getQueryData<{ permissions: string[] }>(['me']);
  const canEdit = me?.permissions.includes('scheduling.write') ?? false;
  const modules = useQuery({ queryKey: ['modules'], queryFn: api.modules });
  const withTime = modules.data?.some((m) => m.key === 'projectdata' && m.active) ?? false;
  const data = useQuery({ queryKey: ['schedule-overview'], queryFn: overviewApi.schedule });
  const hours = useQuery({ queryKey: ['time-overview'], queryFn: overviewApi.time, enabled: withTime });
  const setTarget = useMutation({
    mutationFn: ({ row, date }: { row: ScheduleOverviewRow; date: string | null }) => {
      const step = guardedStep(`Zieltermin ${row.number}`, 'PUT', undoPaths.schedulePlan(row.projectId), { targetFinish: row.targetFinish }, { targetFinish: date });
      return undo.recordAfter(scheduleApi.updatePlan(row.projectId, { targetFinish: date }), step);
    },
    onSettled: async (_r, _e, v) => {
      await queryClient.invalidateQueries({ queryKey: ['schedule-overview'] });
      await queryClient.invalidateQueries({ queryKey: ['plan', v.row.projectId] });
      await queryClient.invalidateQueries({ queryKey: ['schedule', v.row.projectId] });
    },
  });

  if (data.isPending) return <Center h={200}><Loader /></Center>;
  if (data.isError) return <Alert color="red">Die Terminübersicht konnte nicht geladen werden.</Alert>;
  const rows = data.data;
  const planned = rows.filter((r) => r.hasSchedule);
  const hoursOf = new Map((hours.data?.projects ?? []).map((p) => [p.projectId, p]));
  const dates = planned.flatMap((r) => [r.start, r.finish, r.targetFinish, r.baseline?.start, r.baseline?.finish]).filter((d): d is string => !!d);
  const from = dates.length ? time(dates.sort()[0]!) : 0;
  const to = dates.length ? time(dates.sort().slice(-1)[0]!) + DAY : 1;
  const x = (iso: string): number => ((time(iso) - from) / (to - from)) * 100;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
      <Stack gap="md" pb="xl">
        <PageHeader title="Termin- und Dauervergleich – alle Projekte" description="Alle Projekte auf einer Zeitachse: Plan gegen Ziel und aktiven Basisplan, dazu Fortschritt und Stunden. Der Zieltermin lässt sich hier setzen." />
        {setTarget.error && <Alert color="red" withCloseButton onClose={() => setTarget.reset()}>{setTarget.error instanceof ApiError ? setTarget.error.message : 'Speichern fehlgeschlagen.'}</Alert>}

        {planned.length > 0 && (
          <Section title="Zeitachse" description="Balken = Plan, Rahmen = Basisplan, roter Strich = Zieltermin, senkrechte Linie = heute.">
            <Stack gap={6}>
              {planned.map((r) => (
                <Group key={r.projectId} gap="sm" wrap="nowrap">
                  <Anchor size="sm" fw={600} w={110} style={{ flexShrink: 0 }} {...linkTo(navigate, `/projekte/${r.projectId}/termine`)}>{r.number}</Anchor>
                  <div style={{ position: 'relative', flex: 1, height: 22, background: 'var(--mantine-color-default-hover)', borderRadius: 4 }}>
                    {r.baseline && (
                      <div title={`Basisplan ${r.baseline.name}: ${formatDate(r.baseline.start)} – ${formatDate(r.baseline.finish)}`} style={{ position: 'absolute', top: 2, bottom: 2, left: `${x(r.baseline.start)}%`, width: `${Math.max(0.5, x(r.baseline.finish) - x(r.baseline.start))}%`, border: '1px dashed var(--mantine-color-dimmed)', borderRadius: 3 }} />
                    )}
                    {r.start && r.finish && (
                      <div title={`Plan: ${formatDate(r.start)} – ${formatDate(r.finish)}`} style={{ position: 'absolute', top: 6, bottom: 6, left: `${x(r.start)}%`, width: `${Math.max(0.5, x(r.finish) - x(r.start) + 100 * DAY / (to - from))}%`, background: r.floatToTarget !== null && r.floatToTarget < 0 ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-brand-6)', borderRadius: 3 }} />
                    )}
                    {r.targetFinish && <div title={`Ziel ${formatDate(r.targetFinish)}`} style={{ position: 'absolute', top: 0, bottom: 0, left: `${x(r.targetFinish)}%`, width: 2, background: 'var(--mantine-color-red-7)' }} />}
                    {today >= dates[0]! && time(today) < to && <div style={{ position: 'absolute', top: -2, bottom: -2, left: `${x(today)}%`, width: 1, background: 'var(--mantine-color-text)' }} />}
                  </div>
                </Group>
              ))}
              <Group justify="space-between" pl={122}>
                <Text size="xs" c="dimmed">{formatDate(new Date(from).toISOString())}</Text>
                <Text size="xs" c="dimmed">{formatDate(new Date(to - DAY).toISOString())}</Text>
              </Group>
            </Stack>
          </Section>
        )}

        <Section title="Vergleich" description="Tage sind Kalendertage. Abweichung: um so viele Tage liegt das geplante Ende nach (+) oder vor (−) dem Basisplan.">
          <Table.ScrollContainer minWidth={1350}>
            <Table verticalSpacing={4} className="pst-tabular" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Projekt</Table.Th>
                  <Table.Th w={100}>Beginn</Table.Th>
                  <Table.Th w={100}>Ende (Plan)</Table.Th>
                  <Table.Th w={130}>Zieltermin</Table.Th>
                  <Table.Th w={90} ta="right">Puffer</Table.Th>
                  <Table.Th w={100}>Ende (Basis)</Table.Th>
                  <Table.Th w={90} ta="right">Abweichung</Table.Th>
                  <Table.Th w={110} ta="right">Dauer Plan / Basis</Table.Th>
                  <Table.Th w={150}>Fortschritt</Table.Th>
                  {withTime && <Table.Th w={140} ta="right">Stunden / Budget</Table.Th>}
                  <Table.Th w={220}>Nächster Meilenstein</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((r) => {
                  const h = hoursOf.get(r.projectId);
                  const doneShare = r.tasks.total ? r.tasks.done / r.tasks.total : 0;
                  return (
                    <Table.Tr key={r.projectId}>
                      <Table.Td>
                        <Anchor size="sm" fw={600} {...linkTo(navigate, `/projekte/${r.projectId}/termine`)}>{r.number}</Anchor>
                        <Text size="xs" c="dimmed" lineClamp={1} title={r.name}>{r.name}</Text>
                      </Table.Td>
                      {!r.hasSchedule ? (
                        <Table.Td colSpan={8}><Text size="sm" c="dimmed">noch kein Terminplan</Text></Table.Td>
                      ) : (
                        <>
                          <Table.Td><Text size="sm">{formatDate(r.start)}</Text></Table.Td>
                          <Table.Td><Text size="sm" fw={600}>{formatDate(r.finish)}</Text>{r.stale && <Badge size="xs" color="gray" variant="light">nicht gerechnet</Badge>}</Table.Td>
                          <Table.Td><DateCell value={r.targetFinish} disabled={!canEdit} title="Zieltermin setzen" onCommit={(d) => setTarget.mutate({ row: r, date: d || null })} /></Table.Td>
                          <Table.Td ta="right"><Text size="sm" c={r.floatToTarget !== null && r.floatToTarget < 0 ? 'red.7' : undefined} fw={r.floatToTarget !== null && r.floatToTarget < 0 ? 700 : undefined}>{r.floatToTarget === null ? '' : `${signed(r.floatToTarget)} T`}</Text></Table.Td>
                          <Table.Td><Text size="sm" c="dimmed">{r.baseline ? formatDate(r.baseline.finish) : '—'}</Text></Table.Td>
                          <Table.Td ta="right"><Text size="sm" c={r.finishDelta !== null && r.finishDelta > 0 ? 'red.7' : r.finishDelta !== null && r.finishDelta < 0 ? 'green.7' : 'dimmed'}>{r.finishDelta === null ? '' : `${signed(r.finishDelta)} T`}</Text></Table.Td>
                          <Table.Td ta="right"><Text size="sm">{r.durationDays} T{r.baseline ? ` / ${r.baseline.durationDays} T` : ''}</Text></Table.Td>
                          <Table.Td>
                            <Tooltip label={`${r.tasks.done} von ${r.tasks.total} Einträgen erledigt, ${r.tasks.running} laufen, ${r.tasks.blocked} blockiert, ${r.tasks.critical} kritisch offen, ${r.tasks.overdue} über „spätestens“`} multiline w={260}>
                              <Group gap={6} wrap="nowrap">
                                <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'var(--mantine-color-default-border)', overflow: 'hidden' }}>
                                  <div style={{ width: `${doneShare * 100}%`, height: '100%', background: 'var(--mantine-color-brand-6)' }} />
                                </div>
                                <Text size="xs" c={r.tasks.overdue ? 'red.7' : 'dimmed'} style={{ whiteSpace: 'nowrap' }}>{formatPercent(doneShare * 100)}{r.tasks.overdue ? ` · ${r.tasks.overdue}!` : ''}</Text>
                              </Group>
                            </Tooltip>
                          </Table.Td>
                        </>
                      )}
                      {withTime && (
                        <Table.Td ta="right">
                          <Text size="sm" c={h && h.budget && h.total > h.budget ? 'red.7' : undefined}>{h ? `${formatHours(h.total)}${h.budget ? ` / ${formatHours(h.budget)}` : ''}` : ''}</Text>
                        </Table.Td>
                      )}
                      <Table.Td>{r.nextMilestone ? <Text size="sm" lineClamp={1} title={r.nextMilestone.name}>{formatDate(r.nextMilestone.date)} · {r.nextMilestone.code} {r.nextMilestone.name}</Text> : <Text size="sm" c="dimmed">—</Text>}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Section>
      </Stack>
    </div>
  );
}
